import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext.jsx";
import { API_BASE } from "../../api.js";
import { fixImageUrl } from "../../utils/fixImageUrl.js";
import logoImg from "../Images/logo40.jpg";
import "./Chat.css";

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function Avatar({ src, name, size = 44 }) {
  const [err, setErr] = useState(false);
  const initials = name
    ? name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()
    : "?";
  const gradients = [
    "linear-gradient(135deg,#C0392B,#922B21)",
    "linear-gradient(135deg,#1E5631,#C0392B)",
    "linear-gradient(135deg,#C9960C,#C0392B)",
    "linear-gradient(135deg,#12100E,#1E5631)",
  ];
  const bg = gradients[(name ? name.charCodeAt(0) : 0) % 4];

  if (!src || err) {
    return (
      <div
        className="ch-avatar ch-avatar-ph"
        style={{ width: size, height: size, fontSize: size * 0.38, background: bg }}
        title={name}
      >
        {initials}
      </div>
    );
  }
  return (
    <img
      src={fixImageUrl(src)}
      alt={name}
      className="ch-avatar"
      style={{ width: size, height: size }}
      onError={() => setErr(true)}
      title={name}
    />
  );
}

function Bubble({ msg, isOwn, onDelete }) {
  return (
    <div className={`ch-bubble-row${isOwn ? " ch-bubble-row-own" : ""}`}>
      {!isOwn && (
        <a href={`/ArtistProfile/${msg.artistId}`} className="ch-bubble-avatar-link">
          <Avatar src={msg.artistAvatar} name={msg.artistName} size={42} />
        </a>
      )}
      <div className="ch-bubble-col">
        {!isOwn && (
          <div className="ch-bubble-meta">
            <a href={`/ArtistProfile/${msg.artistId}`} className="ch-bubble-name">{msg.artistName}</a>
            <span className="ch-bubble-time">{timeAgo(msg.createdAt)}</span>
          </div>
        )}
        <div className={`ch-bubble${isOwn ? " ch-bubble-own" : ""}`}>
          <p className="ch-bubble-text" dir="auto">{msg.text}</p>
          {isOwn && (
            <div className="ch-bubble-own-footer">
              <span className="ch-bubble-time-own">{timeAgo(msg.createdAt)}</span>
              <button className="ch-bubble-delete" onClick={() => onDelete(msg._id)} title="Delete">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                  <path d="M10 11v6M14 11v6"/>
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
      {isOwn && (
        <a href={`/ArtistProfile/${msg.artistId}`} className="ch-bubble-avatar-link">
          <Avatar src={msg.artistAvatar} name={msg.artistName} size={42} />
        </a>
      )}
    </div>
  );
}

export default function Chat() {
  const { artist, token, isLoggedIn } = useAuth();
  const [messages, setMessages] = useState([]);
  const [draft, setDraft]       = useState("");
  const [loading, setLoading]   = useState(true);
  const [sending, setSending]   = useState(false);
  const [err, setErr]           = useState("");
  const [newCount, setNewCount] = useState(0);
  const bottomRef = useRef(null);
  const feedRef   = useRef(null);
  const lastCount = useRef(0);
  const atBottom  = useRef(true);

  const scrollToBottom = (smooth = true) => {
    const el = feedRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  };

  const fetchMessages = useCallback(async () => {
    try {
      const res  = await axios.get(`${API_BASE}/messages`);
      const msgs = [...res.data].reverse();
      if (msgs.length > lastCount.current) {
        const added = msgs.length - lastCount.current;
        if (atBottom.current) { setNewCount(0); setMessages(msgs); setTimeout(scrollToBottom, 60); }
        else                  { setNewCount(added); setMessages(msgs); }
        lastCount.current = msgs.length;
      } else if (lastCount.current === 0) {
        setMessages(msgs);
        lastCount.current = msgs.length;
        setTimeout(() => scrollToBottom(false), 80);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchMessages();
    const id = setInterval(fetchMessages, 3000);
    return () => clearInterval(id);
  }, [fetchMessages]);

  const handleScroll = () => {
    const el = feedRef.current;
    if (!el) return;
    atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    if (atBottom.current) setNewCount(0);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!draft.trim() || sending) return;
    setSending(true); setErr("");
    try {
      const res = await axios.post(`${API_BASE}/messages`, { text: draft.trim() },
        { headers: { Authorization: `Bearer ${token}` } });
      setMessages((prev) => { const next = [...prev, res.data]; lastCount.current = next.length; return next; });
      setDraft("");
      setTimeout(scrollToBottom, 60);
    } catch (ex) { setErr(ex.response?.data?.error || "Failed to send."); }
    finally { setSending(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE}/messages/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setMessages((prev) => { const next = prev.filter((m) => m._id !== id); lastCount.current = next.length; return next; });
    } catch { setErr("Could not delete."); }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(e); }
  };

  return (
    <div className="ch-page">
      <div className="ch-layout">

        {/* ══ COLUMN 1: Ad / Poster panel ══ */}
        <div className="ch-ad-panel">
            <div className="ch-poster-art">
              <div className="ch-poster-badge">إعلان · AD SPACE</div>
              <div className="ch-poster-lines"><span /><span /><span /></div>
              <div className="ch-poster-center">
                <div className="ch-poster-circle" />
                <p className="ch-poster-ar">تجمع التشكيليين السودانيين</p>
                <p className="ch-poster-en">SUDANESE PLASTIC ARTISTS RALLY</p>
              </div>
              <div className="ch-poster-bottom-strip">
                <span>SPAR HUB</span>
                <span>2024 – 2025</span>
              </div>
            </div>

            <div className="ch-booking">
              <h3 className="ch-booking-title">
                احجز مساحتك الإعلانية
                <span>Reserve Your Space</span>
              </h3>
              <p className="ch-booking-sub">
                ادعم مجتمع التشكيليين السودانيين وأعلن عن فعالياتك وأعمالك
              </p>
              <div className="ch-booking-slots">
                <div className="ch-slot"><span className="ch-slot-icon">🎨</span><span>معرض فني · Art Exhibition</span></div>
                <div className="ch-slot"><span className="ch-slot-icon">🏛️</span><span>ورشة عمل · Workshop</span></div>
                <div className="ch-slot"><span className="ch-slot-icon">📢</span><span>فعالية ثقافية · Cultural Event</span></div>
                <div className="ch-slot"><span className="ch-slot-icon">🖼️</span><span>عرض أعمال · Portfolio Showcase</span></div>
              </div>
              <a href="mailto:sign2art@gmail.com?subject=SPAR Hub Advertising Inquiry" className="ch-book-btn">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                <span>
                  <strong>تواصل للحجز · Book Now</strong>
                  <em>sign2art@gmail.com</em>
                </span>
              </a>
              <p className="ch-booking-note">
                العائد يدعم مجتمع الفنانين السودانيين · All proceeds support the SPAR community
              </p>
            </div>
        </div>

        {/* ══ COLUMN 2: Info paragraph — left of the chat ══ */}
        <div className="ch-below" dir="rtl">
            {isLoggedIn && (
              <div className="ch-me-card">
                <Avatar src={artist?.profileImage} name={artist?.name} size={38} />
                <div>
                  <p className="ch-me-name">{artist?.name}</p>
                  <p className="ch-me-role">عضو · Member</p>
                </div>
                <a href="/Dashboard" className="ch-me-dash-btn">لوحتي ←</a>
              </div>
            )}
            <div className="ch-guidelines">
              <h4 className="ch-guidelines-title">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                قواعد المجتمع · Guidelines
              </h4>
              <p className="ch-guidelines-para">
                شارك أعمالك والأخبار الفنية واحترم الآخرين ولا إعلانات مزعجة.
                يمكنك حذف رسائلك الخاصة في أي وقت.
                <br/>
                Share artwork and art news, respect others, no spam.
                You may delete your own posts at any time.
              </p>
            </div>
        </div>{/* /ch-below */}

        {/* ══ COLUMN 3: Chat board ══ */}
        <div className="ch-card">

          {/* Topbar with logo on the right */}
          <div className="ch-topbar">
            <div className="ch-topbar-brand">
              <div className="ch-topbar-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <div>
                <h1 className="ch-topbar-title">لوحة المجتمع · Community Board</h1>
                <p className="ch-topbar-sub">SPAR Hub · تجمع التشكيليين</p>
              </div>
            </div>

            {/* Status + logo on the right */}
            <div className="ch-topbar-right">
              <div className="ch-topbar-status">
                <span className="ch-pulse-dot" />
                <span className="ch-status-text">Live</span>
                <span className="ch-msg-count">{messages.length}</span>
              </div>
              <img src={logoImg} alt="SPAR Hub" className="ch-topbar-logo" />
            </div>
          </div>

          {/* Feed */}
          <div className="ch-feed" ref={feedRef} onScroll={handleScroll}>
            {loading ? (
              <div className="ch-center"><div className="ch-spinner" /><span>Loading…</span></div>
            ) : messages.length === 0 ? (
              <div className="ch-center">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#C0392B" strokeWidth="1" strokeLinecap="round" style={{ opacity: 0.35 }}>
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                <p>لا توجد رسائل · No posts yet</p>
              </div>
            ) : (
              messages.map((msg) => (
                <Bubble key={msg._id} msg={msg}
                  isOwn={!!(artist && msg.artistId === artist._id)}
                  onDelete={handleDelete} />
              ))
            )}
            <div ref={bottomRef} style={{ height: 6 }} />
          </div>

          {newCount > 0 && (
            <button className="ch-new-nudge" onClick={() => { setNewCount(0); scrollToBottom(); }}>
              ↓ {newCount} رسالة جديدة · new
            </button>
          )}

          {/* Compose */}
          <div className="ch-compose-bar">
            {isLoggedIn ? (
              <>
                <Avatar src={artist?.profileImage} name={artist?.name} size={34} />
                <form className="ch-compose-form" onSubmit={handleSend}>
                  {err && <div className="ch-compose-err">{err}</div>}
                  <div className="ch-compose-input-row">
                    <textarea
                      className="ch-compose-input"
                      placeholder="اكتب هنا… / Write here…"
                      dir="auto"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={handleKey}
                      rows={1}
                      maxLength={1200}
                    />
                    <button type="submit" className="ch-send-btn" disabled={!draft.trim() || sending}>
                      {sending
                        ? <div className="ch-spinner" style={{ width: 15, height: 15, borderWidth: 2 }} />
                        : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <line x1="22" y1="2" x2="11" y2="13"/>
                            <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                          </svg>
                      }
                    </button>
                  </div>
                  <div className="ch-compose-hint">Enter للنشر · {draft.length}/1200</div>
                </form>
              </>
            ) : (
              <div className="ch-guest-bar">
                <a href="/login" className="ch-guest-link">سجّل دخولك / Sign in</a>
                {" "}أو / or{" "}
                <a href="/signup" className="ch-guest-link">أنشئ حساباً / Sign up</a>
                {" "}للمشاركة
              </div>
            )}
          </div>

        </div>{/* /ch-card */}
      </div>{/* /ch-layout */}
    </div>
  );
}
